export interface TaskNode {
  id: string;
  dependsOn: string[];
}

type VisitState = 'unvisited' | 'in_progress' | 'done';

/**
 * Detect circular dependencies in a task graph using DFS.
 * Returns { valid: true } if no cycle, or { valid: false, cycle: string[] } with the cycle path.
 */
export function validateDependencies(tasks: TaskNode[]): { valid: boolean; cycle?: string[] } {
  const adjacency = new Map<string, string[]>();
  const taskIds = new Set<string>();

  for (const task of tasks) {
    adjacency.set(task.id, task.dependsOn);
    taskIds.add(task.id);
  }

  const visitState = new Map<string, VisitState>();
  for (const task of tasks) {
    visitState.set(task.id, 'unvisited');
  }

  const path: string[] = [];

  function dfs(nodeId: string): string[] | null {
    const state = visitState.get(nodeId);
    if (state === 'done') return null;
    if (state === 'in_progress') {
      // Cycle found — return the cycle path from where it starts
      const cycleStart = path.indexOf(nodeId);
      return [...path.slice(cycleStart), nodeId];
    }

    visitState.set(nodeId, 'in_progress');
    path.push(nodeId);

    const deps = adjacency.get(nodeId) ?? [];
    for (const depId of deps) {
      if (!taskIds.has(depId)) continue; // Skip deps outside this template
      const cycle = dfs(depId);
      if (cycle) return cycle;
    }

    path.pop();
    visitState.set(nodeId, 'done');
    return null;
  }

  for (const task of tasks) {
    if (visitState.get(task.id) === 'unvisited') {
      const cycle = dfs(task.id);
      if (cycle) return { valid: false, cycle };
    }
  }

  return { valid: true };
}

/**
 * Topologically sort tasks so that all dependencies come before dependents.
 * Assumes no cycles (call validateDependencies first).
 * Returns task IDs in dependency-first order.
 */
export function topologicalSort(tasks: TaskNode[]): string[] {
  const adjacency = new Map<string, string[]>();
  const taskIds = new Set<string>();

  for (const task of tasks) {
    adjacency.set(task.id, task.dependsOn);
    taskIds.add(task.id);
  }

  const visited = new Set<string>();
  const result: string[] = [];

  function visit(nodeId: string): void {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const deps = adjacency.get(nodeId) ?? [];
    for (const depId of deps) {
      if (taskIds.has(depId)) visit(depId);
    }
    result.push(nodeId);
  }

  for (const task of tasks) {
    visit(task.id);
  }

  return result;
}
